import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  DialogContentText,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Person, 
  Phone, 
  MedicalServices, 
  PersonAdd,
  Add,
  Search,
  MoreVert,
  EmailOutlined,
  Edit,
  DeleteOutline,
  CheckCircleOutline
} from '@mui/icons-material';
import axios from 'axios';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import AgreementStatusPanel from '../../components/Agreements/AgreementStatusPanel';

interface PatientItem {
  id: string;
  name: string;
  age: number;
  phone: string;
  email: string;
  lastVisit: string;
  status: string;
  diagnosis: string;
  nextAppointment?: string;
  assignedTherapist?: string;
  assignedTherapistId?: string;

  // Agreements & Consent
  consentFormSigned?: boolean;
  consentFormSignature?: string;
  consentFormDate?: string;
  treatmentAgreementSigned?: boolean;
  treatmentAgreementSignature?: string;
  treatmentAgreementDate?: string;
  hipaaAuthorized?: boolean;
  hipaaSignature?: string;
  hipaaDate?: string;
  privacyPolicyAcknowledged?: boolean;
  privacyPolicySignature?: string;
  privacyPolicyDate?: string;
}

const PatientManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { state } = useAuth();
  const isTherapist = state.user?.role === 'therapist';
  const [patients, setPatients] = useState<PatientItem[]>([]);
  const [, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    activePatients: 0,
    thisWeek: 0,
    nextWeek: 0
  });

  useEffect(() => {
    void loadPatients();
    void loadTherapists();
  }, []);

  const loadTherapists = async () => {
    try {
      // Fetch therapists (users with role ADMIN or THERAPIST)
      const response = await apiClient.get('/auth/?role=ADMIN,THERAPIST');
      const therapistList = response.data.results.map((user: any) => ({
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
      }));
      setTherapists(therapistList);
    } catch (error) {
      console.error('Error loading therapists:', error);
      setTherapists([]);
    }
  };

  const loadPatients = async () => {
    try {
      setLoading(true);
      const patientsRes = await apiClient.get('/api/patients/');
      
      // Transform backend data to match PatientItem interface
      const transformedPatients = patientsRes.data.results.map((patient: any) => ({
        id: patient.id,
        name: `${patient.first_name} ${patient.last_name}`,
        age: patient.date_of_birth ? 
          new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear() : 0,
        phone: patient.phone_number || patient.phone || '',
        email: patient.email,
        lastVisit: patient.updated_at || patient.created_at,
        status: patient.is_active ? 'active' : 'inactive',
        diagnosis: patient.medical_history || '',
        nextAppointment: '',
        assignedTherapist: patient.assigned_therapist_name || '',
        assignedTherapistId: patient.assigned_therapist_id || '',
        consentFormSigned: patient.consent_form_signed || false,
        consentFormSignature: patient.consent_form_signature || '',
        consentFormDate: patient.consent_form_date || '',
        treatmentAgreementSigned: patient.treatment_agreement_signed || false,
        treatmentAgreementSignature: patient.treatment_agreement_signature || '',
        treatmentAgreementDate: patient.treatment_agreement_date || '',
        hipaaAuthorized: patient.hipaa_authorized || false,
        hipaaSignature: patient.hipaa_signature || '',
        hipaaDate: patient.hipaa_date || '',
        privacyPolicyAcknowledged: patient.privacy_policy_acknowledged || false,
        privacyPolicySignature: patient.privacy_policy_signature || '',
        privacyPolicyDate: patient.privacy_policy_date || '',
      }));
      
      setPatients(transformedPatients);
      setStats({
        totalPatients: patientsRes.data.count || transformedPatients.length,
        activePatients: transformedPatients.filter((p: PatientItem) => p.status === 'active').length,
        thisWeek: 0,
        nextWeek: 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading patients:', error);
      setPatients([]);
      setStats({
        totalPatients: 0,
        activePatients: 0,
        thisWeek: 0,
        nextWeek: 0
      });
      setLoading(false);
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientItem | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [actionPatient, setActionPatient] = useState<PatientItem | null>(null);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [removePatientOpen, setRemovePatientOpen] = useState(false);
  const [completePatientOpen, setCompletePatientOpen] = useState(false);
  const [therapists, setTherapists] = useState<Array<{ id: string; name: string }>>([]);
  const [assignTherapistOpen, setAssignTherapistOpen] = useState(false);
  const [selectedTherapistId, setSelectedTherapistId] = useState<string>('');
  const [editPatientData, setEditPatientData] = useState({
    // Personal
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    // Address
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    // Insurance
    insurance_provider: '',
    insurance_policy_number: '',
    insurance_group_number: '',
    insurance_member_id: '',
    // Medical
    primary_diagnosis: '',
    medical_history: '',
    allergies: '',
    current_medications: '',
    // Therapist
    assigned_therapist: '',
  });
  const [editPatientTab, setEditPatientTab] = useState(0);
  
  const [newPatientData, setNewPatientData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    diagnosis: '',
    assigned_therapist: '',
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.diagnosis.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenActions = (event: React.MouseEvent<HTMLElement>, patient: PatientItem) => {
    setActionAnchorEl(event.currentTarget);
    setActionPatient(patient);
    setSelectedPatient(patient);
  };

  const handleCloseActions = () => {
    setActionAnchorEl(null);
  };

  const handleResendEmail = async () => {
    if (!actionPatient) return;

    try {
      await apiClient.post(`/api/patients/${actionPatient.id}/resend_welcome_email/`);
      setSnackbar({ open: true, message: `Registration email resent to ${actionPatient.email}`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to resend registration email', severity: 'error' });
    } finally {
      handleCloseActions();
    }
  };

  const handleOpenEditPatient = () => {
    if (!actionPatient) return;

    const nameParts = actionPatient.name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');

    // Pre-fill with list-view data synchronously so the dialog opens with visible content
    setEditPatientData({
      first_name: firstName,
      last_name: lastName,
      email: actionPatient.email,
      phone: actionPatient.phone,
      date_of_birth: '',
      gender: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relation: '',
      insurance_provider: '',
      insurance_policy_number: '',
      insurance_group_number: '',
      insurance_member_id: '',
      primary_diagnosis: actionPatient.diagnosis || '',
      medical_history: '',
      allergies: '',
      current_medications: '',
      assigned_therapist: actionPatient.assignedTherapistId || '',
    });
    setEditPatientTab(0);
    setEditPatientOpen(true);
    handleCloseActions();
  };

  // When the edit dialog opens, fetch the full patient details to fill all tabs
  useEffect(() => {
    if (!editPatientOpen || !actionPatient) return;
    const patientId = actionPatient.id;
    const fetchFullPatient = async () => {
      try {
        const res = await apiClient.get(`/api/patients/${patientId}`);
        const p = res.data;
        setEditPatientData(prev => ({
          ...prev,
          first_name: p.first_name || prev.first_name,
          last_name: p.last_name || prev.last_name,
          email: p.email || prev.email,
          phone: p.phone || prev.phone,
          date_of_birth: p.date_of_birth ? p.date_of_birth.split('T')[0] : prev.date_of_birth,
          gender: p.gender && p.gender !== 'Not specified' ? p.gender : prev.gender,
          address: p.street || p.address || prev.address,
          city: p.city || prev.city,
          state: p.state || prev.state,
          zip_code: p.zip_code || prev.zip_code,
          country: p.country || prev.country,
          emergency_contact_name: p.emergency_contact_name || prev.emergency_contact_name,
          emergency_contact_phone: p.emergency_contact_phone || prev.emergency_contact_phone,
          emergency_contact_relation: p.emergency_contact_relationship || prev.emergency_contact_relation,
          insurance_provider: p.insurance_provider || prev.insurance_provider,
          insurance_policy_number: p.insurance_policy_number || prev.insurance_policy_number,
          insurance_group_number: p.insurance_group_number || prev.insurance_group_number,
          insurance_member_id: p.insurance_member_id || prev.insurance_member_id,
          primary_diagnosis: p.primary_diagnosis || prev.primary_diagnosis,
          medical_history: p.medical_history || prev.medical_history,
          allergies: p.allergies || prev.allergies,
          current_medications: p.current_medications || prev.current_medications,
          assigned_therapist: p.primary_therapist || prev.assigned_therapist,
        }));
      } catch (err) {
        console.error('Failed to load full patient details:', err);
      }
    };
    void fetchFullPatient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPatientOpen]);

  const handleSaveEditPatient = async () => {
    if (!actionPatient) return;

    try {
      // Backend updatePatient accepts camelCase fields for the Patient model
      await apiClient.patch(`/api/patients/${actionPatient.id}/`, {
        dateOfBirth: editPatientData.date_of_birth || undefined,
        gender: editPatientData.gender || undefined,
        phone: editPatientData.phone.trim() || undefined,
        address: editPatientData.address.trim() || undefined,
        city: editPatientData.city.trim() || undefined,
        state: editPatientData.state.trim() || undefined,
        zipCode: editPatientData.zip_code.trim() || undefined,
        country: editPatientData.country.trim() || undefined,
        emergencyContactName: editPatientData.emergency_contact_name.trim() || undefined,
        emergencyContactPhone: editPatientData.emergency_contact_phone.trim() || undefined,
        emergencyContactRelation: editPatientData.emergency_contact_relation.trim() || undefined,
        insuranceProvider: editPatientData.insurance_provider.trim() || undefined,
        insurancePolicyNumber: editPatientData.insurance_policy_number.trim() || undefined,
        insuranceGroupNumber: editPatientData.insurance_group_number.trim() || undefined,
        medicalHistory: editPatientData.medical_history.trim() || undefined,
        allergies: editPatientData.allergies.trim() || undefined,
        currentMedications: editPatientData.current_medications.trim() || undefined,
        assignedTherapistId: editPatientData.assigned_therapist || null,
      });

      const therapistName = editPatientData.assigned_therapist
        ? (therapists.find((t) => t.id === editPatientData.assigned_therapist)?.name || '')
        : '';

      setPatients(prev => prev.map(p =>
        p.id === actionPatient.id
          ? {
              ...p,
              phone: editPatientData.phone.trim(),
              diagnosis: editPatientData.primary_diagnosis.trim(),
              assignedTherapist: therapistName,
              assignedTherapistId: editPatientData.assigned_therapist,
            }
          : p
      ));

      setSnackbar({ open: true, message: 'Client updated successfully', severity: 'success' });
      setEditPatientOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to update client', severity: 'error' });
    }
  };

  const handleOpenRemovePatient = () => {
    setRemovePatientOpen(true);
    handleCloseActions();
  };

  const handleRemovePatient = async () => {
    if (!actionPatient) return;

    try {
      await apiClient.delete(`/api/patients/${actionPatient.id}/`);
      setPatients(prev => prev.filter(patient => patient.id !== actionPatient.id));
      setSnackbar({ open: true, message: 'Client removed successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove client', severity: 'error' });
    } finally {
      setRemovePatientOpen(false);
      setActionPatient(null);
    }
  };

  const handleOpenCompletePatient = () => {
    setCompletePatientOpen(true);
    handleCloseActions();
  };

  const handleCompletePatient = async () => {
    if (!actionPatient) return;

    try {
      await apiClient.patch(`/api/patients/${actionPatient.id}/`, {
        status: 'discharged',
        discharge_date: new Date().toISOString().split('T')[0],
      });

      setPatients(prev => prev.map(patient => (
        patient.id === actionPatient.id
          ? { ...patient, status: 'discharged' }
          : patient
      )));

      setSnackbar({ open: true, message: 'Client marked complete (sessions complete)', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to mark client complete', severity: 'error' });
    } finally {
      setCompletePatientOpen(false);
      setActionPatient(null);
    }
  };

  const handleOpenAssignTherapist = () => {
    setSelectedTherapistId(actionPatient?.assignedTherapistId || '');
    setAssignTherapistOpen(true);
    handleCloseActions();
  };

  const handleAssignTherapist = async () => {
    if (!actionPatient) return;
    try {
      await apiClient.patch(`/api/patients/${actionPatient.id}/`, {
        assignedTherapistId: selectedTherapistId || null,
      });
      const therapistName = selectedTherapistId
        ? (therapists.find((t) => t.id === selectedTherapistId)?.name || '')
        : '';
      setPatients((prev) =>
        prev.map((p) =>
          p.id === actionPatient.id
            ? { ...p, assignedTherapist: therapistName, assignedTherapistId: selectedTherapistId }
            : p
        )
      );
      setSnackbar({
        open: true,
        message: selectedTherapistId
          ? `Therapist assigned to ${actionPatient.name} successfully`
          : `Therapist removed from ${actionPatient.name}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to assign therapist', severity: 'error' });
    } finally {
      setAssignTherapistOpen(false);
      setActionPatient(null);
    }
  };

  const getStatusColor = (status: string): 'success' | 'default' => {
    return status === 'active' ? 'success' : 'default';
  };

  const handleCreatePatient = async () => {
    try {
      setIsLoading(true);
      setFormErrors({});

      const errors: Record<string, string> = {};
      if (!newPatientData.first_name.trim()) errors.first_name = 'Required';
      if (!newPatientData.last_name.trim()) errors.last_name = 'Required';
      if (!newPatientData.email.trim()) {
        errors.email = 'Required';
      } else {
        // Email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newPatientData.email)) {
          errors.email = 'Invalid email format';
        }
      }
      
      // Date of birth validation
      if (newPatientData.date_of_birth) {
        const dob = new Date(newPatientData.date_of_birth);
        const today = new Date();
        if (dob > today) {
          errors.date_of_birth = 'Date of birth cannot be in the future';
        }
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setIsLoading(false);
        return;
      }

      // Generate username from email or name with timestamp to ensure uniqueness
      const generateUsername = (email: string, firstName: string, lastName: string): string => {
        const timestamp = Date.now().toString().slice(-6);
        if (email && email.includes('@')) {
          const emailPrefix = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '_');
          return `${emailPrefix}_${timestamp}`;
        }
        return `${firstName}.${lastName}_${timestamp}`.toLowerCase().replace(/[^a-z0-9_.]/g, '_');
      };

      const username = generateUsername(
        newPatientData.email,
        newPatientData.first_name,
        newPatientData.last_name
      );

      // Use correct field names matching backend API
      const patientData = {
        username,
        email: newPatientData.email.toLowerCase().trim(),
        firstName: newPatientData.first_name.trim(),
        lastName: newPatientData.last_name.trim(),
        phoneNumber: newPatientData.phone.trim() || undefined,
        dateOfBirth: newPatientData.date_of_birth || undefined,
        assignedTherapistId: newPatientData.assigned_therapist || undefined,
        medicalHistory: newPatientData.diagnosis.trim() || undefined,
      };

      console.log('Creating patient:', patientData);

      const response = await apiClient.post('/api/patients/', patientData);
      console.log('Patient created:', response.data);

      setSnackbar({ open: true, message: 'Client added successfully. Registration email sent.', severity: 'success' });
      setAddPatientOpen(false);
      setNewPatientData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        diagnosis: '',
        assigned_therapist: '',
      });
      
      // Refresh the patient list to show the new patient
      await loadPatients();
    } catch (error: unknown) {
      console.error('Failed to add client:', error);

      if (axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
        const backendErrors = error.response.data as Record<string, unknown>;
        const formattedErrors: Record<string, string> = {};

        Object.keys(backendErrors).forEach((key) => {
          const errorValue = backendErrors[key];
          if (Array.isArray(errorValue) && errorValue.length > 0) {
            formattedErrors[key] = String(errorValue[0]);
          } else if (typeof errorValue === 'string') {
            formattedErrors[key] = errorValue;
          }
        });

        setFormErrors(formattedErrors);
        
        // Extract error message from backend
        let errorMessage = 'Failed to add client';
        if (typeof backendErrors.error === 'string') {
          errorMessage = backendErrors.error;
        } else if (typeof backendErrors.detail === 'string') {
          errorMessage = backendErrors.detail;
        } else if (typeof backendErrors.message === 'string') {
          errorMessage = backendErrors.message;
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: axios.isAxiosError(error) && error.message 
            ? `Failed to add client: ${error.message}` 
            : 'Failed to add client', 
          severity: 'error' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isTherapist ? 'My Clients' : 'Client Management'}
        </Typography>
        {!isTherapist && (
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setAddPatientOpen(true)}
            fullWidth={isMobile}
          >
            Add New Client
          </Button>
        )}
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search clients by name or diagnosis..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Patient Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.totalPatients}</Typography>
              <Typography variant="body2">Total Clients</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.activePatients}</Typography>
              <Typography variant="body2">Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.thisWeek}</Typography>
              <Typography variant="body2">This Week</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.nextWeek}</Typography>
              <Typography variant="body2">Next Week</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Patients List */}
      {isMobile ? (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {filteredPatients.length === 0 ? (
            <Paper sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography color="text.secondary">No clients found.</Typography>
            </Paper>
          ) : (
            filteredPatients.map((patient) => (
              <Card key={patient.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar>{patient.name.charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="subtitle1">{patient.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Age: {patient.age}</Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={(event) => handleOpenActions(event, patient)}>
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Chip
                    label={patient.status}
                    color={getStatusColor(patient.status)}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1.25 }}
                  />

                  <Typography variant="body2"><strong>Therapist:</strong> {patient.assignedTherapist || 'Unassigned'}</Typography>
                  <Typography variant="body2"><strong>Diagnosis:</strong> {patient.diagnosis || 'Not specified'}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {patient.phone || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {patient.email || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Last Visit:</strong> {patient.lastVisit || 'N/A'}</Typography>
                  <Typography variant="body2"><strong>Next Appointment:</strong> {patient.nextAppointment || 'Not scheduled'}</Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Last Visit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Therapist</TableCell>
                  <TableCell>Primary Diagnosis</TableCell>
                  <TableCell>Next Appointment</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPatients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar>{patient.name.charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="subtitle2">{patient.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Age: {patient.age}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{patient.phone}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {patient.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{patient.lastVisit}</TableCell>
                    <TableCell>
                      <Chip
                        label={patient.status}
                        color={getStatusColor(patient.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {patient.assignedTherapist ? (
                        <Chip
                          icon={<PersonAdd fontSize="small" />}
                          label={patient.assignedTherapist}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>{patient.diagnosis}</TableCell>
                    <TableCell>{patient.nextAppointment || 'Not scheduled'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenActions(event, patient)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActions}
      >
        <MenuItem onClick={() => void handleResendEmail()}>
          <ListItemIcon>
            <EmailOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Resend Email</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenEditPatient}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Client</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenAssignTherapist}>
          <ListItemIcon>
            <PersonAdd fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Add Therapist</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenCompletePatient}>
          <ListItemIcon>
            <CheckCircleOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Complete</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenRemovePatient} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutline fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove Client</ListItemText>
        </MenuItem>
      </Menu>

      {/* Patient Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Client Details - {selectedPatient?.name}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_event: React.SyntheticEvent, newValue: number) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto">
            <Tab label="Overview" />
            <Tab label="Medical History" />
            <Tab label="Treatment Plan" />
            <Tab label="Notes" />
            <Tab label="Agreements" />
          </Tabs>

          {tabValue === 0 && selectedPatient && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Personal Information
                      </Typography>
                      <Typography><strong>Name:</strong> {selectedPatient.name}</Typography>
                      <Typography><strong>Age:</strong> {selectedPatient.age}</Typography>
                      <Typography><strong>Status:</strong> {selectedPatient.status}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Contact Information
                      </Typography>
                      <Typography><strong>Phone:</strong> {selectedPatient.phone}</Typography>
                      <Typography><strong>Email:</strong> {selectedPatient.email}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <MedicalServices sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Clinical Information
                      </Typography>
                      <Typography><strong>Primary Diagnosis:</strong> {selectedPatient.diagnosis}</Typography>
                      <Typography><strong>Last Visit:</strong> {selectedPatient.lastVisit}</Typography>
                      <Typography><strong>Next Appointment:</strong> {selectedPatient.nextAppointment || 'Not scheduled'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 3 }}>
              <Typography>Medical history details would be displayed here...</Typography>
            </Box>
          )}

          {tabValue === 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography>Treatment plan details would be displayed here...</Typography>
            </Box>
          )}

          {tabValue === 3 && (
            <Box sx={{ mt: 3 }}>
              <Typography>Clinical notes would be displayed here...</Typography>
            </Box>
          )}

          {tabValue === 4 && selectedPatient && (
            <AgreementStatusPanel patient={selectedPatient as any} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editPatientOpen}
        onClose={() => setEditPatientOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Client
          {actionPatient && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {actionPatient.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Tabs
            value={editPatientTab}
            onChange={(_e, v) => setEditPatientTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Personal" />
            <Tab label="Address" />
            <Tab label="Emergency Contact" />
            <Tab label="Insurance" />
            <Tab label="Medical" />
          </Tabs>

          {/* Tab 0 — Personal */}
          {editPatientTab === 0 && (
            <Grid container spacing={2} sx={{ pt: 1, pb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={editPatientData.first_name}
                  InputProps={{ readOnly: true }}
                  helperText="Name changes require an admin account update"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={editPatientData.last_name}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" value={editPatientData.email}
                  InputProps={{ readOnly: true }}
                  helperText="Email changes require an admin account update"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" value={editPatientData.phone}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Date of Birth" type="date"
                  value={editPatientData.date_of_birth}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select value={editPatientData.gender} label="Gender"
                    onChange={(e) => setEditPatientData(prev => ({ ...prev, gender: e.target.value }))}
                  >
                    <MenuItem value=""><em>Not specified</em></MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="Non-binary">Non-binary</MenuItem>
                    <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assigned Therapist</InputLabel>
                  <Select value={editPatientData.assigned_therapist} label="Assigned Therapist"
                    onChange={(e) => setEditPatientData(prev => ({ ...prev, assigned_therapist: e.target.value }))}
                  >
                    <MenuItem value=""><em>None (Unassign)</em></MenuItem>
                    {therapists.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Tab 1 — Address */}
          {editPatientTab === 1 && (
            <Grid container spacing={2} sx={{ pt: 1, pb: 2 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" value={editPatientData.address}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, address: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" value={editPatientData.city}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, city: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="State" value={editPatientData.state}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, state: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="ZIP Code" value={editPatientData.zip_code}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Country" value={editPatientData.country}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, country: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 2 — Emergency Contact */}
          {editPatientTab === 2 && (
            <Grid container spacing={2} sx={{ pt: 1, pb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Name" value={editPatientData.emergency_contact_name}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Phone" value={editPatientData.emergency_contact_phone}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Relationship" value={editPatientData.emergency_contact_relation}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, emergency_contact_relation: e.target.value }))}
                  placeholder="e.g. Spouse, Parent, Sibling"
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 3 — Insurance */}
          {editPatientTab === 3 && (
            <Grid container spacing={2} sx={{ pt: 1, pb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Insurance Provider" value={editPatientData.insurance_provider}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, insurance_provider: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Policy Number" value={editPatientData.insurance_policy_number}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, insurance_policy_number: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Group Number" value={editPatientData.insurance_group_number}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, insurance_group_number: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Member ID" value={editPatientData.insurance_member_id}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, insurance_member_id: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 4 — Medical */}
          {editPatientTab === 4 && (
            <Grid container spacing={2} sx={{ pt: 1, pb: 2 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Primary Diagnosis" value={editPatientData.primary_diagnosis}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, primary_diagnosis: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Medical History" value={editPatientData.medical_history}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, medical_history: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Allergies" value={editPatientData.allergies}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, allergies: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Current Medications" value={editPatientData.current_medications}
                  onChange={(e) => setEditPatientData(prev => ({ ...prev, current_medications: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPatientOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSaveEditPatient()}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removePatientOpen}
        onClose={() => setRemovePatientOpen(false)}
      >
        <DialogTitle>Remove Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {actionPatient?.name || 'this client'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovePatientOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleRemovePatient()}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={completePatientOpen}
        onClose={() => setCompletePatientOpen(false)}
      >
        <DialogTitle>Complete Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark {actionPatient?.name || 'this client'} as complete? This will set status to discharged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletePatientOpen(false)}>Cancel</Button>
          <Button color="success" variant="contained" onClick={() => void handleCompletePatient()}>
            Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Therapist Dialog */}
      <Dialog
        open={assignTherapistOpen}
        onClose={() => setAssignTherapistOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Assign Therapist
          {actionPatient && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Client: {actionPatient.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Therapist</InputLabel>
              <Select
                value={selectedTherapistId}
                label="Select Therapist"
                onChange={(e) => setSelectedTherapistId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None (Unassign)</em>
                </MenuItem>
                {therapists.map((therapist) => (
                  <MenuItem key={therapist.id} value={therapist.id}>
                    {therapist.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTherapistOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => void handleAssignTherapist()}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Client Dialog */}
      <Dialog 
        open={addPatientOpen} 
        onClose={() => setAddPatientOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Client</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={newPatientData.first_name}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, first_name: e.target.value }))}
                  error={!!formErrors.first_name}
                  helperText={formErrors.first_name}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={newPatientData.last_name}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, last_name: e.target.value }))}
                  error={!!formErrors.last_name}
                  helperText={formErrors.last_name}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newPatientData.email}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, email: e.target.value }))}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newPatientData.phone}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, phone: e.target.value }))}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  placeholder="(555) 123-4567"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={newPatientData.date_of_birth}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  error={!!formErrors.date_of_birth}
                  helperText={formErrors.date_of_birth}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Primary Diagnosis"
                  value={newPatientData.diagnosis}
                  onChange={(e) => setNewPatientData(prev => ({ ...prev, diagnosis: e.target.value }))}
                  error={!!formErrors.diagnosis}
                  helperText={formErrors.diagnosis}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.assigned_therapist}>
                  <InputLabel>Assigned Therapist (Optional)</InputLabel>
                  <Select
                    value={newPatientData.assigned_therapist}
                    label="Assigned Therapist (Optional)"
                    onChange={(e) => setNewPatientData(prev => ({ ...prev, assigned_therapist: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {therapists.map((therapist) => (
                      <MenuItem key={therapist.id} value={therapist.id}>
                        {therapist.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Note:</strong> A registration link will be emailed to the client so they can create their own username and password.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddPatientOpen(false);
            setNewPatientData({
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              date_of_birth: '',
              diagnosis: '',
              assigned_therapist: '',
            });
            setFormErrors({});
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              void handleCreatePatient();
            }}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <Add />}
          >
            {isLoading ? 'Adding...' : 'Add Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientManagement;
